import { useEffect, useState } from "react";
import {
  Card,
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  Radio,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./roles-permissions-tab.css";
import EditRolesPermissions from "./edit-roles-permissions/edit-roles-permissions";
import { permissionAccessLevels } from "../../../../../utils/Constant";

type props = {
  data: any;
  isMyOrganisation?: boolean;
};

export default function RolesPermissionsTab({ data, isMyOrganisation }: props) {
  const [showEditRoles, setShowEditRoles] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  const handleEditRoles = () => {
    setShowEditRoles(true);
  };

  useEffect(() => {
    if (data) {
      parsePermissions(data.roles);
    }
  }, [data]);

  const parsePermissions = (selectedRoles: any[]) => {
    if (selectedRoles?.length > 0) {
      const categories: any[] = [];
      const categoryNames: string[] = [];
      selectedRoles.map((role: any) =>
        role.permissions.forEach((permission: any) => {
          if (!categoryNames.includes(permission.category.name)) {
            categoryNames.push(permission.category.name);
          }
        })
      );

      categoryNames.forEach((categoryName) => {
        const filterPermissions = selectedRoles?.map((role: any) =>
          role.permissions.filter(
            (permission: any) => permission.category.name === categoryName
          )
        );
        const permissions = filterPermissions.flat(1);
        const uniquePermissions: any[] = [];
        permissions.forEach((permission) => {
          // filter unique permission with highest accessLevel
          if (uniquePermissions.length > 0) {
            let isAvailable = false;
            uniquePermissions.forEach((uniquePermission, index) => {
              if (permission.resourceId === uniquePermission.resourceId) {
                isAvailable = true;
                if (uniquePermission.accessLevel < permission.accessLevel) {
                  uniquePermissions[index] = {
                    ...uniquePermission,
                    accessLevel: permission.accessLevel,
                  };
                }
              }
            });
            if (!isAvailable) uniquePermissions.push(permission);
          } else {
            uniquePermissions.push(permission);
          }
        });
        categories.push({
          name: categoryName,
          permissions: uniquePermissions,
        });
      });
      // console.log("categories - ", categories);
      setCategories(categories);
    }
  };

  return (
    <>
      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Roles</Typography>
          {!isMyOrganisation && (
            <Button
              variant="text"
              startIcon={<EditOutlinedIcon />}
              onClick={handleEditRoles}
            >
              Edit
            </Button>
          )}
        </Box>
        <Box className="user-name-layout">
          <Typography className="label-txt">Role(s)</Typography>
          <Box>
            {data?.roles.map((role: any, index: number) => (
              <Typography key={role.id} sx={{ mt: index === 0 ? 0 : 0.5 }}>
                {role.name}
              </Typography>
            ))}
          </Box>
        </Box>
      </Card>

      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Permissions</Typography>
        </Box>
        <Box sx={{ mt: 3, px: 1.5 }}>
          {categories?.length > 0 ? (
            <Box
              sx={{
                width: "calc(100% - 192px)",
                marginLeft: 24,
                display: "flex",
              }}
            >
              <Typography
                sx={{ flex: 1, textAlign: "start", fontWeight: "bold" }}
              >
                Permission
              </Typography>
              <Typography
                sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
              >
                Full Access
              </Typography>
              <Typography
                sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
              >
                View Only
              </Typography>
              <Typography
                sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
              >
                None
              </Typography>
            </Box>
          ) : (
            <Typography>No Permissions Available</Typography>
          )}
          {categories?.map((category: any, index: number) => {
            return (
              <Box key={category.name}>
                {index > 0 && <Divider />}
                <Box className="user-name-layout">
                  <Typography className="label-txt" sx={{ mt: 2 }}>
                    {category.name}
                  </Typography>
                  <Box display={"block"} sx={{ width: "calc(100% - 192px)" }}>
                    {category.permissions.map((item: any, index: number) => (
                      <Box key={item.key}>
                        <Box className="permission-row">
                          <Box className="permission-title">
                            <Typography>{item.name}</Typography>
                            {index === 0 && (
                              <Tooltip
                                title="Description for more complicated and involved permissions"
                                arrow
                                placement="top"
                              >
                                <IconButton>
                                  <InfoOutlinedIcon color="primary" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Radio
                            className="permission-radio"
                            value="full-access"
                            name="radio-buttons"
                            inputProps={{ "aria-label": "A" }}
                            checked={
                              item.accessLevel ===
                              permissionAccessLevels.fullAccess
                            }
                            disabled
                          />
                          <Radio
                            className="permission-radio"
                            value="view-only"
                            name="radio-buttons"
                            inputProps={{ "aria-label": "B" }}
                            checked={
                              item.accessLevel ===
                              permissionAccessLevels.viewOnly
                            }
                            disabled
                          />
                          <Radio
                            className="permission-radio"
                            value="none"
                            name="radio-buttons"
                            inputProps={{ "aria-label": "C" }}
                            checked={
                              item.accessLevel ===
                              permissionAccessLevels.noAccess
                            }
                            disabled
                          />
                        </Box>
                        {index !== category.permissions.length - 1 && (
                          <Divider />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Card>

      {showEditRoles && (
        <EditRolesPermissions
          showDialog={showEditRoles}
          closeDialog={() => setShowEditRoles(false)}
          data={data}
        />
      )}
    </>
  );
}
